import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import ServiceManager from 'services/ServiceManager';
import AdapterError from 'errors/AdapterError';
import { useFormatting } from 'locale';
import { APIVersion } from 'additiv-services';
import { useNavigate } from 'react-router-dom';
import { adaptInvestmentOverview } from '../adapters/adaptInvestmentOverview';
import { portfolioActions } from '../../../constants';
import { useManagerProfile } from '../../../../../prodivers/managerProfile';
import { getActivePortfoliosIDs } from '../../../../../utils/filterPortfolios';

type TUseWithInvestmentOverview = {
    clientId: number,
    dfsClientId: string,
    isDataAccessible: boolean
    reFetchPortfolios?: boolean | null
}

type TRawData = {
    data: any,
    products: Array<any | never> | undefined,
    proposals?: any
}

type TData = {
    overview?: any,
    portfolios?: any,
    sectionedPortfolios?: any,
    investmentAllocation?: {
        chart: Array<any>
    }
}

const EMPTY_DATA: TData = {
    overview: {},
    investmentAllocation: { chart: [{ data: { pieSa: {}, bar: {}, list: {} } }] },
};

export const useWithInvestmentOverview = ({
    clientId, dfsClientId: dfsId, isDataAccessible, reFetchPortfolios = null,
}: TUseWithInvestmentOverview) => {
    const navigate = useNavigate();
    const { i18n: { language }, t } = useTranslation();
    const { getFormattedCurrency } = useFormatting();
    const { hasAccessPolicy } = useManagerProfile();

    const fetchInvestmentOverview = async (): Promise<TRawData> => {
        const params = { language };

        const [
            dataInvestmentResponse,
            allPortfoliosResponse,
            allJointPortfoliosResponse,
            allPOAPortfoliosResponse,
            allPAPortfoliosResponse,
            historicalPerfomanceResponse,
            products,
        ] = await Promise.all([
            ServiceManager.portfolioManagement('getInvestmentOverview', [clientId, params, APIVersion.V11]),
            ServiceManager.portfolioManagement('getPortfolios', [{ contactId: clientId, ...params }]),
            ServiceManager.portfolioManagement('getJointPortfolios', [clientId, params]),
            ServiceManager.portfolioManagement('getPOAPortfolios', [clientId, params]),
            ServiceManager.portfolioManagement('getPAPortfolios', [clientId, params]),
            ServiceManager.portfolioManagement('getHistoricalPerformance', [clientId]),
            ServiceManager.customCommonService('getProductsConfiguration', [{ language, contactId: clientId }]),
        ]) as any;

        const activePortfoliosIDs = getActivePortfoliosIDs(allPortfoliosResponse?.data || []);

        try {
            return {
                data: {
                    ...dataInvestmentResponse?.data,
                    Portfolios: allPortfoliosResponse?.data.filter((portfolio) => activePortfoliosIDs.some((activePortfolio) => activePortfolio === portfolio.Id)),
                    JointPortfolios: allJointPortfoliosResponse?.data.filter((portfolio) => activePortfoliosIDs.some((activePortfolio) => activePortfolio === portfolio.id)),
                    POAPortfolios: allPOAPortfoliosResponse?.data.filter((portfolio) => activePortfoliosIDs.some((activePortfolio) => activePortfolio === portfolio.id)),
                    PAPortfolios: allPAPortfoliosResponse?.data.filter((portfolio) => activePortfoliosIDs.some((activePortfolio) => activePortfolio === portfolio.id)),
                    HistoricalPerformance: [
                        {
                            SeriesType: 'Portfolio',
                            Values: historicalPerfomanceResponse?.data?.Values,
                        },
                    ],
                },
                products: products?.data?.productOffers[0].products,
            };
        } catch (err) {
            throw new AdapterError(err);
        }
    };

    const {
        data: rawData,
        isLoading,
        error,
        refetch: getInvestmentOverview,
    } = useQuery<TRawData>({
        queryKey: ['investmentOverview', clientId, language],
        queryFn: fetchInvestmentOverview,
        enabled: !!clientId && reFetchPortfolios !== false,
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 60 * 10, // 10 hours
    });

    const data = useMemo<TData>(() => {
        if (!rawData) return EMPTY_DATA;

        return adaptInvestmentOverview({
            data: rawData.data,
            dfsId,
            portfolioActions,
            t,
            getFormattedCurrency,
            products: rawData.products,
            hasAccessPolicy,
            isDataAccessible,
            navigatem: navigate,
        });
    }, [rawData, hasAccessPolicy, dfsId, t, getFormattedCurrency, isDataAccessible, navigate]);

    return {
        data, isLoading, error, getInvestmentOverview,
    };
};
